import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import useResizeObserver from 'use-resize-observer';
import type { Tab } from '../../../organisms/map-interactive-states/react/types';
import Icon from '../../../_particles/icon/react/Icon';
import { useMediaQuery } from '../../../../javascripts/hooks/useMediaquery';
import { MEDIA_QUERIES } from '../../../../javascripts/constants';

export type Props = {
  activeTab: string,
  onTabClick: (tab: Tab) => void;
  tabs: Tab[],
  className?: string;
}

const TabBar = ({
  tabs, activeTab, onTabClick, className,
}: Props) => {
  const isDesktop = useMediaQuery(MEDIA_QUERIES.headernav);
  const [renderSelect, setRenderSelect] = useState(!isDesktop);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const { ref: tabBarRef, width: tabBarWidth } = useResizeObserver();
  const tabItemsRef = useRef(null);
  const { width: tabItemsWidth } = useResizeObserver({ ref: tabItemsRef });
  const [indicatorStyle, setInidicatorStyle] = useState({ width: '0', transform: 'translateX(0)' });

  useEffect(() => {
    if (tabBarWidth < tabItemsRef.current.offsetWidth || !isDesktop) {
      setRenderSelect(true);
      setIsOpen(false);
    } else {
      setRenderSelect(false);
      setIsOpen(true);
    }
  }, [tabBarWidth]);

  useEffect(() => {
    if (!renderSelect && tabItemsRef) {
      const i = tabs.findIndex(item => item.tabtitle === activeTab);
      if (i > -1) {
        const $item = tabItemsRef.current.querySelectorAll('.tab-bar__list-item')[i];

        if ($item) {
          const { left: parentLeft } = containerRef.current.getBoundingClientRect();
          const { width, left } = $item.getBoundingClientRect();
          setInidicatorStyle({ width: `${width}px`, transform: `translateX(${left - parentLeft}px)` });
        }
      }
    }
  }, [renderSelect, tabItemsWidth, activeTab]);

  const handleTabClick = (tab: Tab) => {
    onTabClick(tab);
    if (renderSelect) {
      setIsOpen(false);
    }
  };

  return (
    <div className={classNames('tab-bar', `tab-bar--${renderSelect ? 'menu' : 'list'}`, className)} ref={tabBarRef}>
      {renderSelect &&
        <div className='tab-bar__select-wrap'>
          <button
            type='button'
            className='tab-bar__select'
            aria-haspopup='true'
            aria-expanded={isOpen}
            onClick={() => setIsOpen(!isOpen)}>
              <span className='u-hidden-visually'>Inhalt auswählen</span>
              <span className='tab-bar__select-current' aria-hidden={!isOpen}>
                <span className='tab-bar__select-text'>{activeTab}</span>
              </span>
              <Icon icon='arrow-down' className='tab-bar__select-icon'/>

          </button>
        </div>
      }
      <div className='tab-bar__inner' ref={containerRef}>
      <ul className='tab-bar__list' hidden={!isOpen} ref={tabItemsRef}>
        {tabs.map(tab => (
          <li className="tab-bar__list-item" key={tab.tabtitle}>
            <button
              className={classNames(
                'tab-bar__item',
                activeTab === tab && 'tab-bar__item--active',
              )}
              onClick={() => handleTabClick(tab)}
            >
              <span className="tab-bar__text">
                {tab.tabtitle}
              </span>
            </button>
          </li>
        ))}
      </ul>
      </div>
      <div className='tab-bar__active-indicator' style={indicatorStyle}></div>
    </div>
  );
};

export default TabBar;
